import React, { useEffect, useRef, useState } from 'react';
import { useAds } from '../../contexts/AdContext';
import { isWeb } from '../../utils/platform';
import adService from '../../services/adService';
import './AdBanner.css';

/**
 * Issue #204: Platform-aware Ad Banner Component
 * Uses Google's official test ad unit IDs to display real test ads.
 * Replace test IDs with production IDs after AdSense/AdMob account approval.
 */

// Google's official test ad slot IDs for AdSense
// These render Google's "Test Ad" placeholder banners
const TEST_AD_CLIENT = 'ca-pub-3940256099942544';

function AdBanner({ slot }) {
  const { isInitialized, demoMode, consentGiven } = useAds();
  const adRef = useRef(null);
  const adPushed = useRef(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isInitialized) return;

    if (!demoMode && isWeb() && adRef.current && !adPushed.current) {
      try {
        adService.showBanner();
        adPushed.current = true;
      } catch (error) {
        // Silent failure - AC-7, AC-11
      }
    }
  }, [isInitialized, demoMode]);

  if (!isInitialized || dismissed) {
    return null;
  }

  // Demo mode: show sample ad with NPA/Personalized indicator
  if (demoMode) {
    const isPersonalized = consentGiven === true;
    const adTypeLabel = isPersonalized ? 'Personalized Ad' : 'NPA Ad';
    const adColor = isPersonalized ? '#4CAF50' : '#FF9800';

    return (
      <div className="ad-banner-container" data-ad-slot={slot}>
        <div className="ad-banner-demo">
          <div className="ad-banner-demo-top">
            <span className="ad-banner-demo-label">Anzeige</span>
            <span style={{
              fontSize: '10px',
              fontWeight: 700,
              color: '#fff',
              background: adColor,
              padding: '2px 8px',
              borderRadius: '3px',
            }}>
              {adTypeLabel}
            </span>
            <button className="ad-banner-demo-close" onClick={() => setDismissed(true)} aria-label="Close">&times;</button>
          </div>
          <div className="ad-banner-demo-content">
            <div className="ad-banner-demo-icon" style={{ background: adColor }}>AD</div>
            <div className="ad-banner-demo-text">
              <div className="ad-banner-demo-headline">
                {isPersonalized ? 'Deals speziell für dich!' : 'Allgemeine Studenten-Angebote'}
              </div>
              <div className="ad-banner-demo-description">
                {isPersonalized
                  ? 'Basierend auf deinen Interessen — personalisierte Empfehlungen.'
                  : 'Nicht personalisiert — allgemeine Anzeigen für alle.'}
              </div>
              <div className="ad-banner-demo-footer">
                <span className="ad-banner-demo-advertiser">Sample Ad</span>
                <span className="ad-banner-demo-cta" style={{ color: adColor }}>Mehr erfahren &rsaquo;</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render Google AdSense with test ad IDs
  if (isWeb()) {
    return (
      <div className="ad-banner-container" data-ad-slot={slot}>
        <ins
          className="adsbygoogle"
          style={{ display: 'block' }}
          data-ad-client={TEST_AD_CLIENT}
          data-ad-slot={slot}
          data-ad-format="auto"
          data-adtest="on"
          data-full-width-responsive="true"
          ref={adRef}
        />
      </div>
    );
  }

  return null;
}

export default AdBanner;
